/**
 * chartPatterns.js
 *
 * Algorithmic Double Top / Double Bottom detector for the Liquidity Map.
 *
 * 100% geometric/deterministic. NEVER involves an LLM in deciding
 * whether a pattern exists. NEVER mutates trendlineMath.js — only
 * reuses its swing-point detector.
 *
 * Output statuses are mutually exclusive and explicit:
 *   • "confirmed" → neckline has been broken (cierre claramente por debajo / arriba)
 *   • "forming"   → 3 anchors present, neckline NOT broken yet
 *   • null        → no valid pattern in this candle window
 *
 * The "forming" vs "confirmed" distinction is non-negotiable: same
 * honesty principle as no_testeado / no_evaluado in S/R and Predictor.
 */

import { detectSwings } from "./trendlineMath.js";

// ──────────────────────────────────────────────────────────────────────
// PER-COIN CALIBRATED THRESHOLDS
// ──────────────────────────────────────────────────────────────────────
// Calibrated from 881 real swing-pair samples across 6 coins × 4
// timeframes (15m / 1h / 4h / 1d) via chartPatterns.calibrate.mjs.
//
// WHY PER-COIN?
//   Cross-coin median audit (same methodology already applied to
//   strength_score thresholds and LiqScore Phase-2 factors) revealed
//   real distributional asymmetries between coins:
//
//     • BTC: median diff between consecutive peaks = 1.12%, median
//       valley depth = 2.76%. → "tight peaks, shallow valleys" world.
//     • ZEC: median diff = 2.85%, median valley depth = 6.78%. →
//       "wide peaks, deep valleys" world.
//
//   Using a pooled p25 (0.53%) for similarity would let BTC pass the
//   filter ~40% of the time and ZEC only ~8% → systematic bias toward
//   BTC-dominated pattern detection. The per-coin tables below
//   eliminate that bias by giving each coin its own p25(diff) and
//   p50(depth/ridge) — every coin gets the same "tightest quartile"
//   and "median depth" treatment relative to its own behavior.
//
// Source: /app/frontend/src/components/liqmap/chartPatterns.calibrate.mjs
// Re-run that script if a new coin is added or methodology changes.
const COIN_THRESHOLDS = {
  // peakTol = p25 of |peak2 - peak1| / mean(peaks) * 100 (in %)
  // valleyMin = p50 of (mean(peaks) - valleyLow) / mean(peaks) * 100 (in %)
  // ridgeMin  = p50 of (ridgeHigh - mean(bottoms)) / mean(bottoms) * 100 (in %)
  BTC: { peakTol: 0.3591, valleyMin: 2.7624, ridgeMin: 3.3378 },
  ETH: { peakTol: 0.3996, valleyMin: 3.6318, ridgeMin: 3.5482 },
  SOL: { peakTol: 0.7360, valleyMin: 4.0146, ridgeMin: 5.9175 },
  BNB: { peakTol: 0.3971, valleyMin: 3.5623, ridgeMin: 3.5109 },
  XRP: { peakTol: 0.4463, valleyMin: 4.0137, ridgeMin: 4.2127 },
  ZEC: { peakTol: 0.9281, valleyMin: 6.7830, ridgeMin: 6.3884 },
};

// Fallback for any coin not yet calibrated (e.g. future 7th asset).
// Pooled p25 / p50 across all 881 samples — NOT a guess, just the
// pre-calibration global default. Console-warns once on first use so
// the operator notices and runs the per-coin calibration.
const POOLED_FALLBACK = {
  peakTol: 0.5299,
  valleyMin: 3.9235,
  ridgeMin: 4.2986,
};

// Neckline-break threshold — kept in sync with detectBreak() in
// trendlineMath.js so the project speaks ONE language for "broken".
const BOUNCE_THRESHOLD = 0.005;

// ──────────────────────────────────────────────────────────────────────
// Threshold lookup with explicit fallback signaling.
// ──────────────────────────────────────────────────────────────────────
const _warned = new Set();
export function thresholdsForCoin(coin) {
  const key = (coin || "").toUpperCase();
  const t = COIN_THRESHOLDS[key];
  if (t) return { ...t, calibrated: true, coin: key };
  if (!_warned.has(key) && typeof console !== "undefined") {
    _warned.add(key);
    console.warn(
      `[chartPatterns] No per-coin threshold for "${key}". ` +
      `Using pooled fallback (peakTol=${POOLED_FALLBACK.peakTol}, ` +
      `valleyMin=${POOLED_FALLBACK.valleyMin}, ridgeMin=${POOLED_FALLBACK.ridgeMin}). ` +
      `Run chartPatterns.calibrate.mjs to add this coin to the per-coin table.`
    );
  }
  return { ...POOLED_FALLBACK, calibrated: false, coin: key };
}

// ──────────────────────────────────────────────────────────────────────
// Main detector.
//
// Selection rule: scan pivots NEWEST → OLDEST and return the first
// valid pair found. Same conceptual rule as the trendline's "tail-most
// recent" pick — chosen by user for cross-tool consistency.
// ──────────────────────────────────────────────────────────────────────
export function detectDoubleTopBottom(candles, coin) {
  if (!Array.isArray(candles) || candles.length < 10) {
    return { pattern: null, status: null, reason: "not_enough_candles" };
  }
  const th = thresholdsForCoin(coin);
  const { highs, lows } = detectSwings(candles, 3);

  // ── Double TOP — newest swing high paired with an older swing high.
  for (let j = highs.length - 1; j >= 1; j--) {
    for (let i = j - 1; i >= 0; i--) {
      const p1 = highs[i], p2 = highs[j];
      if (p2.i <= p1.i) continue;
      const meanP = (p1.p + p2.p) / 2;
      const diffPct = Math.abs(p2.p - p1.p) / meanP * 100;
      if (diffPct > th.peakTol) continue;
      // Lowest LOW strictly between the two peaks.
      let valleyLow = Infinity;
      let valleyIdx = -1;
      for (let k = p1.i + 1; k < p2.i; k++) {
        if (candles[k].l < valleyLow) { valleyLow = candles[k].l; valleyIdx = k; }
      }
      if (!Number.isFinite(valleyLow) || valleyIdx < 0) continue;
      const depthPct = (meanP - valleyLow) / meanP * 100;
      if (depthPct < th.valleyMin) continue;

      // Neckline = valley low. Post-anchor evaluation in a SINGLE loop:
      // at each candle k we check BOTH the expected break (close below
      // neckline = confirmation) AND the contrary break (close above the
      // higher of the two peaks = invalidation of the bearish thesis).
      // Whichever fires first chronologically wins; the other side
      // stops being considered once a status is set.
      const neckline = valleyLow;
      const confirmLevel = neckline * (1 - BOUNCE_THRESHOLD);
      const peakMax = Math.max(p1.p, p2.p);
      const invalidLevel = peakMax * (1 + BOUNCE_THRESHOLD);
      let breakIdx = null, breakPrice = null;
      let invalidationIdx = null, invalidationPrice = null;
      for (let k = p2.i + 1; k < candles.length; k++) {
        const c = candles[k].c;
        if (c <= confirmLevel) { breakIdx = k; breakPrice = c; break; }
        if (c >= invalidLevel) { invalidationIdx = k; invalidationPrice = c; break; }
      }
      const status = breakIdx != null
        ? "confirmed"
        : invalidationIdx != null
          ? "invalidated"
          : "forming";
      return {
        pattern: "double_top",
        status,
        peak1: { i: p1.i, p: p1.p, t: p1.t },
        valley: { i: valleyIdx, p: valleyLow, t: candles[valleyIdx].t },
        peak2: { i: p2.i, p: p2.p, t: p2.t },
        neckline,
        breakIdx, breakPrice,
        invalidationIdx, invalidationPrice,
        invalidationLevel: invalidLevel,
        metrics: { diffPct, depthPct },
        thresholds: th,
        reason: null,
      };
    }
  }

  // ── Double BOTTOM — mirror of the above for swing lows.
  for (let j = lows.length - 1; j >= 1; j--) {
    for (let i = j - 1; i >= 0; i--) {
      const b1 = lows[i], b2 = lows[j];
      if (b2.i <= b1.i) continue;
      const meanB = (b1.p + b2.p) / 2;
      const diffPct = Math.abs(b2.p - b1.p) / meanB * 100;
      if (diffPct > th.peakTol) continue;
      let ridgeHigh = -Infinity;
      let ridgeIdx = -1;
      for (let k = b1.i + 1; k < b2.i; k++) {
        if (candles[k].h > ridgeHigh) { ridgeHigh = candles[k].h; ridgeIdx = k; }
      }
      if (!Number.isFinite(ridgeHigh) || ridgeIdx < 0) continue;
      const heightPct = (ridgeHigh - meanB) / meanB * 100;
      if (heightPct < th.ridgeMin) continue;

      // Symmetric to Double Top: single post-anchor loop evaluates both
      // the bullish confirmation (close above ridge = neckline) AND the
      // bearish invalidation (close below the lower of the two bottoms).
      // Whichever fires first wins.
      const neckline = ridgeHigh;
      const confirmLevel = neckline * (1 + BOUNCE_THRESHOLD);
      const bottomMin = Math.min(b1.p, b2.p);
      const invalidLevel = bottomMin * (1 - BOUNCE_THRESHOLD);
      let breakIdx = null, breakPrice = null;
      let invalidationIdx = null, invalidationPrice = null;
      for (let k = b2.i + 1; k < candles.length; k++) {
        const c = candles[k].c;
        if (c >= confirmLevel) { breakIdx = k; breakPrice = c; break; }
        if (c <= invalidLevel) { invalidationIdx = k; invalidationPrice = c; break; }
      }
      const status = breakIdx != null
        ? "confirmed"
        : invalidationIdx != null
          ? "invalidated"
          : "forming";
      return {
        pattern: "double_bottom",
        status,
        bottom1: { i: b1.i, p: b1.p, t: b1.t },
        ridge: { i: ridgeIdx, p: ridgeHigh, t: candles[ridgeIdx].t },
        bottom2: { i: b2.i, p: b2.p, t: b2.t },
        neckline,
        breakIdx, breakPrice,
        invalidationIdx, invalidationPrice,
        invalidationLevel: invalidLevel,
        metrics: { diffPct, heightPct },
        thresholds: th,
        reason: null,
      };
    }
  }

  return { pattern: null, status: null, reason: "no_valid_pair" };
}

// ──────────────────────────────────────────────────────────────────────
// TRIANGLE THRESHOLDS — per-coin "approximately flat" slope cap.
// ──────────────────────────────────────────────────────────────────────
// Calibrated from 884 swing-pair |slopeNorm| samples across 6 coins ×
// 4 timeframes via trianglePatterns.calibrate.mjs.
//
// slopeNorm = (b.p - a.p) / a.p / (b.i - a.i) * 100, abs taken,
// expressed in % per candle. "Flat" = |slopeNorm| ≤ this threshold
// (per coin's own p25 — flattest quartile of real lines for that coin).
//
// WHY PER-COIN? Cross-coin audit (n=884) confirmed real volatility
// asymmetry: ZEC's median |slopeNorm| is 1.77× the pooled median
// (real bias, not sample noise — small-n=48 was misleading). Per-coin
// thresholds keep "flat" semantically equivalent across coins: the
// flattest quartile of THAT coin's lines, regardless of its baseline
// volatility level.
const TRIANGLE_FLAT_PER_COIN = {
  BTC: 0.0420,
  ETH: 0.0495,
  SOL: 0.0883,
  BNB: 0.0469,
  XRP: 0.0529,
  ZEC: 0.1164,
};
// Fallback for any uncalibrated coin — pooled p25 across all 884 samples.
const TRIANGLE_FLAT_POOLED = 0.0628;

function triangleFlatFor(coin) {
  const key = (coin || "").toUpperCase();
  if (TRIANGLE_FLAT_PER_COIN[key] != null) {
    return { value: TRIANGLE_FLAT_PER_COIN[key], calibrated: true };
  }
  return { value: TRIANGLE_FLAT_POOLED, calibrated: false };
}

// ──────────────────────────────────────────────────────────────────────
// Triangle detector — ascending / descending / symmetric.
// ──────────────────────────────────────────────────────────────────────
// Geometry:
//   • Ascending  → high-line slope ≈ 0 (flat),  low-line slope > 0
//   • Descending → high-line slope < 0,         low-line slope ≈ 0
//   • Symmetric  → high-line slope < 0,         low-line slope > 0
//                  AND the two lines intersect within ≤ 2× pattern length
//                  ahead (strict convergence per user's choice (a)).
//
// Selection rule: same as the trendline + double-top family — newest
// valid configuration wins. Uses the FULL set of swing highs / lows
// from the most recent block of pivots (≥2 each), with `linePoints`
// returning ALL pivots that belong to each line for the tooltip.
//
// Confirmation: any post-pattern candle CLOSE that breaches either
// line by ≥ BOUNCE_THRESHOLD (0.5%) flips status to "confirmed".
// ──────────────────────────────────────────────────────────────────────
function _twoAnchorLine(a, b) {
  if (b.i <= a.i) return null;
  const m = (b.p - a.p) / (b.i - a.i);
  return { m, b: a.p - m * a.i, fp: a, lp: b };
}
function _absSlopeNorm(a, b) {
  if (b.i <= a.i || a.p <= 0) return Infinity;
  return Math.abs((b.p - a.p) / a.p / (b.i - a.i) * 100);
}

export function detectTriangle(candles, coin) {
  if (!Array.isArray(candles) || candles.length < 12) {
    return { pattern: null, status: null, reason: "not_enough_candles" };
  }
  const { highs, lows } = detectSwings(candles, 3);
  if (highs.length < 2 || lows.length < 2) {
    return { pattern: null, status: null, reason: "not_enough_pivots" };
  }
  const flat = triangleFlatFor(coin);

  // Two-anchor lines using first → last swing high / low. Same
  // convention used by trendline. Triangle "selection = most recent
  // valid" is naturally satisfied because detectSwings returns pivots
  // in time order and we use first..last of the full set.
  const hi0 = highs[0], hiN = highs[highs.length - 1];
  const lo0 = lows[0], loN = lows[lows.length - 1];
  const lineH = _twoAnchorLine(hi0, hiN);
  const lineL = _twoAnchorLine(lo0, loN);
  if (!lineH || !lineL) {
    return { pattern: null, status: null, reason: "degenerate_line" };
  }
  const absH = _absSlopeNorm(hi0, hiN);
  const absL = _absSlopeNorm(lo0, loN);
  const hFlat = absH <= flat.value;
  const lFlat = absL <= flat.value;
  const hUp = lineH.m > 0 && !hFlat;
  const hDn = lineH.m < 0 && !hFlat;
  const lUp = lineL.m > 0 && !lFlat;
  const lDn = lineL.m < 0 && !lFlat;

  let kind = null;
  if (hFlat && lUp)   kind = "ascending";
  else if (hDn && lFlat) kind = "descending";
  else if (hDn && lUp)   kind = "symmetric";
  else {
    return {
      pattern: null, status: null,
      reason: hFlat && lFlat ? "no_convergence_both_flat"
        : (hUp && lUp) || (hDn && lDn) ? "no_convergence_same_direction"
        : "no_pattern",
    };
  }

  // Symmetric: enforce strict convergence — intersection x must lie
  // within (lastPivotIdx, lastPivotIdx + 2 × patternLen).
  const patternStartIdx = Math.min(hi0.i, lo0.i);
  const patternEndIdx = Math.max(hiN.i, loN.i);
  const patternLen = patternEndIdx - patternStartIdx;
  let apexIdx = null;
  if (lineH.m !== lineL.m) {
    apexIdx = (lineL.b - lineH.b) / (lineH.m - lineL.m);
  }
  if (kind === "symmetric") {
    if (apexIdx == null || !Number.isFinite(apexIdx)
        || apexIdx <= patternEndIdx
        || apexIdx > patternEndIdx + 2 * patternLen) {
      return { pattern: null, status: null, reason: "symmetric_apex_too_far" };
    }
  }

  // Confirmation / invalidation — scan post-pattern candles. Triangle
  // breaks when a candle CLOSE breaches either line by ≥ BOUNCE_THRESHOLD.
  // For ASCENDING (bullish bias: flat top + ascending lows), an UP break
  // is confirmation while a DOWN break is INVALIDATION of the bullish
  // thesis. Symmetric mirror for DESCENDING. SYMMETRIC keeps the legacy
  // semantics (any break = confirmation) because there is no directional
  // bias by definition (user decision Punto C, 2026-06-27).
  let breakIdx = null, breakPrice = null, breakDir = null;
  for (let k = patternEndIdx + 1; k < candles.length; k++) {
    const cBar = candles[k];
    const yH = lineH.m * k + lineH.b;
    const yL = lineL.m * k + lineL.b;
    if (cBar.c >= yH * (1 + BOUNCE_THRESHOLD)) {
      breakIdx = k; breakPrice = cBar.c; breakDir = "up"; break;
    }
    if (cBar.c <= yL * (1 - BOUNCE_THRESHOLD)) {
      breakIdx = k; breakPrice = cBar.c; breakDir = "down"; break;
    }
  }
  let status = "forming";
  let invalidationIdx = null, invalidationPrice = null;
  if (breakIdx != null) {
    if (kind === "symmetric") {
      status = "confirmed";
    } else if (kind === "ascending") {
      if (breakDir === "up") {
        status = "confirmed";
      } else {
        status = "invalidated";
        invalidationIdx = breakIdx; invalidationPrice = breakPrice;
        // Hide from confirmed-break fields (they only describe the
        // expected direction) so the renderer can pick the right badge.
        breakIdx = null; breakPrice = null;
      }
    } else { // descending
      if (breakDir === "down") {
        status = "confirmed";
      } else {
        status = "invalidated";
        invalidationIdx = breakIdx; invalidationPrice = breakPrice;
        breakIdx = null; breakPrice = null;
      }
    }
  }

  return {
    pattern: "triangle",
    kind, status,
    highLine: { m: lineH.m, b: lineH.b, slopeNormAbs: absH, points: highs.map((p) => ({ i: p.i, p: p.p, t: p.t })) },
    lowLine:  { m: lineL.m, b: lineL.b, slopeNormAbs: absL, points: lows.map((p)  => ({ i: p.i, p: p.p, t: p.t })) },
    patternStartIdx, patternEndIdx,
    apexIdx: Number.isFinite(apexIdx) ? apexIdx : null,
    breakIdx, breakPrice, breakDir,
    invalidationIdx, invalidationPrice,
    thresholds: { flat: flat.value, calibrated: flat.calibrated, coin: (coin || "").toUpperCase() },
    reason: null,
  };
}

// ──────────────────────────────────────────────────────────────────────
// END Triangle block.
// ──────────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────
// HEAD & SHOULDERS / INVERSE H&S — calibrated per-coin (approved Fase 0)
// ──────────────────────────────────────────────────────────────────────
// 222 triples sampled (HS n=116, InvHS n=106). Cross-coin audit
// confirmed real bias (ZEC head HS 3.35× pooled; BNB head HS 0.37×).
// head_min      = p50(head_diff_pct) of own distribution.
// shoulder_max  = p25(|LS - RS| / mean(LS,RS) * 100) of own distribution.
const HCH_THRESHOLDS = {
  BTC: { head_min: 0.42, shoulder_max: 0.35 },
  ETH: { head_min: 0.55, shoulder_max: 0.21 },
  SOL: { head_min: 0.61, shoulder_max: 0.52 },
  BNB: { head_min: 0.27, shoulder_max: 0.34 },
  XRP: { head_min: 1.29, shoulder_max: 0.44 },
  ZEC: { head_min: 2.37, shoulder_max: 0.41 },
};
const HCH_POOLED = { head_min: 0.71, shoulder_max: 0.39 };

const INV_HCH_THRESHOLDS = {
  BTC: { head_min: 0.90, shoulder_max: 0.31 },
  ETH: { head_min: 0.70, shoulder_max: 0.15 },
  SOL: { head_min: 1.89, shoulder_max: 2.16 },
  BNB: { head_min: 1.27, shoulder_max: 0.44 },
  XRP: { head_min: 2.06, shoulder_max: 0.32 },
  ZEC: { head_min: 1.69, shoulder_max: 0.81 },
};
const INV_HCH_POOLED = { head_min: 1.43, shoulder_max: 0.41 };

function _hchThresholdsFor(coin, table, pooled) {
  const key = (coin || "").toUpperCase();
  if (table[key]) return { ...table[key], calibrated: true, coin: key };
  return { ...pooled, calibrated: false, coin: key };
}

// Common HCH detector — `mode` flips the direction. For HS:
//   pivots = swing highs (extrema), valleys = lowest lows between peaks
//   head must be HIGHER than both shoulders; break = close BELOW neckline.
// For InvHS, it's the mirror: pivots = swing lows, ridges = highest highs,
// head must be LOWER, break = close ABOVE neckline.
function _detectHeadShoulders(candles, coin, mode) {
  if (!Array.isArray(candles) || candles.length < 12) {
    return { pattern: null, status: null, reason: "not_enough_candles" };
  }
  const isHS = mode === "hs";
  const th = _hchThresholdsFor(coin, isHS ? HCH_THRESHOLDS : INV_HCH_THRESHOLDS, isHS ? HCH_POOLED : INV_HCH_POOLED);
  const { highs, lows } = detectSwings(candles, 3);
  const pivots = isHS ? highs : lows;
  if (pivots.length < 3) {
    return { pattern: null, status: null, reason: "not_enough_pivots", thresholds: th };
  }
  // Iterate triples NEWEST → OLDEST so "most recent valid wins".
  for (let k = pivots.length - 1; k >= 2; k--) {
    for (let j = k - 1; j >= 1; j--) {
      for (let i = j - 1; i >= 0; i--) {
        const LS = pivots[i], H = pivots[j], RS = pivots[k];
        // Head must be the extremum.
        if (isHS && (H.p <= LS.p || H.p <= RS.p)) continue;
        if (!isHS && (H.p >= LS.p || H.p >= RS.p)) continue;
        const meanS = (LS.p + RS.p) / 2;
        if (meanS <= 0) continue;
        const head_diff_pct = isHS
          ? (H.p - Math.max(LS.p, RS.p)) / meanS * 100
          : (Math.min(LS.p, RS.p) - H.p) / meanS * 100;
        if (head_diff_pct < th.head_min) continue;
        const shoulder_diff_pct = Math.abs(LS.p - RS.p) / meanS * 100;
        if (shoulder_diff_pct > th.shoulder_max) continue;

        // Neckline anchor 1: extremum BETWEEN LS and H (opposite direction).
        // Neckline anchor 2: extremum BETWEEN H and RS.
        let n1 = isHS ? Infinity : -Infinity, n1i = -1;
        for (let m = LS.i + 1; m < H.i; m++) {
          if (isHS) { if (candles[m].l < n1) { n1 = candles[m].l; n1i = m; } }
          else      { if (candles[m].h > n1) { n1 = candles[m].h; n1i = m; } }
        }
        let n2 = isHS ? Infinity : -Infinity, n2i = -1;
        for (let m = H.i + 1; m < RS.i; m++) {
          if (isHS) { if (candles[m].l < n2) { n2 = candles[m].l; n2i = m; } }
          else      { if (candles[m].h > n2) { n2 = candles[m].h; n2i = m; } }
        }
        if (n1i < 0 || n2i < 0) continue;
        if (!Number.isFinite(n1) || !Number.isFinite(n2)) continue;

        // Diagonal neckline (own slope, NOT horizontal).
        const m_neck = (n2 - n1) / (n2i - n1i);
        const b_neck = n1 - m_neck * n1i;
        const projNeck = (idx) => m_neck * idx + b_neck;

        // Confirmation / invalidation: single post-RS loop. For HCH the
        // expected break is a close BELOW the diagonal neckline; the
        // invalidation is a close ABOVE the head (H.p × (1 + BOUNCE)).
        // For Inverse HCH it's the mirror: close ABOVE neckline = confirm,
        // close BELOW head (H.p × (1 - BOUNCE)) = invalidation. Whichever
        // fires first chronologically wins.
        const headInvalidLevel = isHS
          ? H.p * (1 + BOUNCE_THRESHOLD)
          : H.p * (1 - BOUNCE_THRESHOLD);
        let breakIdx = null, breakPrice = null;
        let invalidationIdx = null, invalidationPrice = null;
        for (let m = RS.i + 1; m < candles.length; m++) {
          const yN = projNeck(m);
          const c = candles[m].c;
          if (isHS) {
            if (c <= yN * (1 - BOUNCE_THRESHOLD)) { breakIdx = m; breakPrice = c; break; }
            if (c >= headInvalidLevel) { invalidationIdx = m; invalidationPrice = c; break; }
          } else {
            if (c >= yN * (1 + BOUNCE_THRESHOLD)) { breakIdx = m; breakPrice = c; break; }
            if (c <= headInvalidLevel) { invalidationIdx = m; invalidationPrice = c; break; }
          }
        }
        const status = breakIdx != null
          ? "confirmed"
          : invalidationIdx != null
            ? "invalidated"
            : "forming";
        return {
          pattern: isHS ? "hs" : "inv_hs",
          status,
          LS:   { i: LS.i, p: LS.p, t: LS.t },
          H:    { i: H.i,  p: H.p,  t: H.t },
          RS:   { i: RS.i, p: RS.p, t: RS.t },
          neck1: { i: n1i, p: n1, t: candles[n1i].t },
          neck2: { i: n2i, p: n2, t: candles[n2i].t },
          neckline: { m: m_neck, b: b_neck },
          breakIdx, breakPrice,
          invalidationIdx, invalidationPrice,
          invalidationLevel: headInvalidLevel,
          metrics: { head_diff_pct, shoulder_diff_pct },
          thresholds: th,
          reason: null,
        };
      }
    }
  }
  // Diagnose why no triple succeeded — peek at the most recent triple.
  const last = pivots.slice(-3);
  if (last.length === 3) {
    const [LS, H, RS] = last;
    const meanS = (LS.p + RS.p) / 2;
    const head_diff_pct = isHS
      ? (H.p - Math.max(LS.p, RS.p)) / meanS * 100
      : (Math.min(LS.p, RS.p) - H.p) / meanS * 100;
    const shoulder_diff_pct = Math.abs(LS.p - RS.p) / meanS * 100;
    if (head_diff_pct < th.head_min) return { pattern: null, status: null, reason: "head_not_prominent", thresholds: th };
    if (shoulder_diff_pct > th.shoulder_max) return { pattern: null, status: null, reason: "shoulders_dissimilar", thresholds: th };
  }
  return { pattern: null, status: null, reason: "no_valid_triple", thresholds: th };
}

export function detectHeadAndShoulders(candles, coin) { return _detectHeadShoulders(candles, coin, "hs"); }
export function detectInverseHeadAndShoulders(candles, coin) { return _detectHeadShoulders(candles, coin, "inv_hs"); }

// ──────────────────────────────────────────────────────────────────────
// CANDLE PATTERNS — calibrated pooled (geometry) + per-coin (trend)
// ──────────────────────────────────────────────────────────────────────
// Cross-check on n=4,800 real candles verified intra-candle ratios are
// structurally neutral across coins (all per-coin ratios in 0.88-1.25×
// of pooled, no flag). Trend drift IS biased (ZEC 1.79×) → per-coin.
const CANDLE_GEOM = {
  doji_body_max: 0.094,
  hammer_lower_min: 0.418,
  hammer_upper_max: 0.109,
  inv_hammer_upper_min: 0.402,
  inv_hammer_lower_max: 0.118,
  engulf_body_ratio_min: 1.48,
};
const TREND_DRIFT_PER_COIN = {
  BTC: 1.23, ETH: 1.51, SOL: 2.38, BNB: 1.27, XRP: 1.57, ZEC: 3.13,
};
const TREND_DRIFT_POOLED = 1.74;
const TREND_LOOKBACK = 10;

function _trendThresholdFor(coin) {
  const key = (coin || "").toUpperCase();
  if (TREND_DRIFT_PER_COIN[key] != null) {
    return { value: TREND_DRIFT_PER_COIN[key], calibrated: true, coin: key };
  }
  return { value: TREND_DRIFT_POOLED, calibrated: false, coin: key };
}

function _candleRatios(c) {
  const range = c.h - c.l;
  if (range <= 0) return null;
  const body = Math.abs(c.c - c.o);
  return {
    range, body,
    bodyR: body / range,
    upWick: (c.h - Math.max(c.o, c.c)) / range,
    loWick: (Math.min(c.o, c.c) - c.l) / range,
    bullish: c.c > c.o,
  };
}

function _medianRange(candles, i, lookback = 20) {
  const start = Math.max(0, i - lookback);
  const arr = [];
  for (let k = start; k < i; k++) {
    const r = candles[k].h - candles[k].l;
    if (r > 0) arr.push(r);
  }
  if (!arr.length) return 0;
  arr.sort((a, b) => a - b);
  return arr[Math.floor(arr.length / 2)];
}

function _trendContext(candles, i, threshold) {
  if (i < TREND_LOOKBACK) return { dir: "neutral", drift: 0 };
  const base = candles[i - TREND_LOOKBACK].c;
  if (base <= 0) return { dir: "neutral", drift: 0 };
  const drift = (candles[i].c - base) / base * 100;
  if (drift > threshold) return { dir: "up", drift };
  if (drift < -threshold) return { dir: "down", drift };
  return { dir: "neutral", drift };
}

// Detect candle patterns. Returns array of `{ i, pattern, signal, ... }`
// per pattern hit. Priority enforced: Engulfing > Doji > Hammer/InvHammer.
// Each candle index is at most ONE entry (priority is exclusive).
export function detectCandlePatterns(candles, coin) {
  if (!Array.isArray(candles) || candles.length < TREND_LOOKBACK + 2) return [];
  const trendTh = _trendThresholdFor(coin);
  const out = [];
  for (let i = 1; i < candles.length; i++) {
    const r = _candleRatios(candles[i]);
    if (!r) continue;
    // Filter: skip tiny candles that yield noisy ratios.
    const med = _medianRange(candles, i);
    if (med > 0 && r.range < med / 2) continue;

    // ── 1) Engulfing (highest priority) ──
    const prev = candles[i - 1];
    const pr = _candleRatios(prev);
    if (pr && pr.bullish !== r.bullish && pr.body > 0 && r.body > 0) {
      const curMax = Math.max(candles[i].o, candles[i].c);
      const curMin = Math.min(candles[i].o, candles[i].c);
      const prvMax = Math.max(prev.o, prev.c);
      const prvMin = Math.min(prev.o, prev.c);
      if (curMax >= prvMax && curMin <= prvMin) {
        const ratio = r.body / pr.body;
        if (ratio >= CANDLE_GEOM.engulf_body_ratio_min) {
          out.push({
            i, pattern: r.bullish ? "engulfing_bull" : "engulfing_bear",
            signal: r.bullish ? "bullish" : "bearish",
            context: "any",
            metrics: { engulf_ratio: ratio, body_ratio: r.bodyR },
            thresholds: { engulf_body_ratio_min: CANDLE_GEOM.engulf_body_ratio_min },
            priority: 1,
          });
          continue;
        }
      }
    }
    // ── 2) Doji ──
    if (r.bodyR <= CANDLE_GEOM.doji_body_max) {
      out.push({
        i, pattern: "doji", signal: "neutral", context: "any",
        metrics: { body_ratio: r.bodyR, upper_wick: r.upWick, lower_wick: r.loWick },
        thresholds: { doji_body_max: CANDLE_GEOM.doji_body_max },
        priority: 2,
      });
      continue;
    }
    // ── 3) Hammer / Hanging Man (long lower wick, small upper) ──
    if (r.loWick >= CANDLE_GEOM.hammer_lower_min && r.upWick <= CANDLE_GEOM.hammer_upper_max) {
      const ctx = _trendContext(candles, i, trendTh.value);
      if (ctx.dir === "down") {
        out.push({ i, pattern: "hammer", signal: "bullish", context: ctx,
          metrics: { lower_wick: r.loWick, upper_wick: r.upWick, body_ratio: r.bodyR, drift_pct: ctx.drift },
          thresholds: { lower_min: CANDLE_GEOM.hammer_lower_min, upper_max: CANDLE_GEOM.hammer_upper_max, trend_th: trendTh.value, calibrated: trendTh.calibrated, coin: trendTh.coin },
          priority: 3 });
        continue;
      } else if (ctx.dir === "up") {
        out.push({ i, pattern: "hanging_man", signal: "bearish", context: ctx,
          metrics: { lower_wick: r.loWick, upper_wick: r.upWick, body_ratio: r.bodyR, drift_pct: ctx.drift },
          thresholds: { lower_min: CANDLE_GEOM.hammer_lower_min, upper_max: CANDLE_GEOM.hammer_upper_max, trend_th: trendTh.value, calibrated: trendTh.calibrated, coin: trendTh.coin },
          priority: 3 });
        continue;
      }
      // neutral context: do not mark (honesty rule)
    }
    // ── 4) Inverted Hammer / Shooting Star ──
    if (r.upWick >= CANDLE_GEOM.inv_hammer_upper_min && r.loWick <= CANDLE_GEOM.inv_hammer_lower_max) {
      const ctx = _trendContext(candles, i, trendTh.value);
      if (ctx.dir === "down") {
        out.push({ i, pattern: "inverted_hammer", signal: "bullish", context: ctx,
          metrics: { upper_wick: r.upWick, lower_wick: r.loWick, body_ratio: r.bodyR, drift_pct: ctx.drift },
          thresholds: { upper_min: CANDLE_GEOM.inv_hammer_upper_min, lower_max: CANDLE_GEOM.inv_hammer_lower_max, trend_th: trendTh.value, calibrated: trendTh.calibrated, coin: trendTh.coin },
          priority: 3 });
        continue;
      } else if (ctx.dir === "up") {
        out.push({ i, pattern: "shooting_star", signal: "bearish", context: ctx,
          metrics: { upper_wick: r.upWick, lower_wick: r.loWick, body_ratio: r.bodyR, drift_pct: ctx.drift },
          thresholds: { upper_min: CANDLE_GEOM.inv_hammer_upper_min, lower_max: CANDLE_GEOM.inv_hammer_lower_max, trend_th: trendTh.value, calibrated: trendTh.calibrated, coin: trendTh.coin },
          priority: 3 });
        continue;
      }
    }
  }
  return out;
}

// Re-exported for tests / debugging.
export const __internals = { COIN_THRESHOLDS, POOLED_FALLBACK, BOUNCE_THRESHOLD, TRIANGLE_FLAT_PER_COIN, TRIANGLE_FLAT_POOLED, HCH_THRESHOLDS, HCH_POOLED, INV_HCH_THRESHOLDS, INV_HCH_POOLED, CANDLE_GEOM, TREND_DRIFT_PER_COIN, TREND_DRIFT_POOLED, TREND_LOOKBACK };
